import { Box, CircularProgress } from "@mui/material";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import { RequireAuth } from "@/app/routes/RequireAuth";
import { RequireTab } from "@/app/routes/RequireTab";
import { useIsAdminHost } from "@/features/admin/hooks/useIsAdminHost";
import { adminRouteElements } from "@/features/admin/routes/AdminRoutes";
import { AdminLayout } from "@/features/admin/ui/AdminLayout";
import { BlankLayout } from "@/shared/ui/layout/BlankLayout";
import { MainLayout } from "@/shared/ui/layout/MainLayout";

const HomePage = lazy(async () => {
  const module = await import("@/features/home/ui/HomePage");
  return { default: module.HomePage };
});

const DataHubPage = lazy(async () => {
  const module = await import("@/features/data-hub/ui/DataHubPage");
  return { default: module.DataHubPage };
});

const DomainDetailPage = lazy(async () => {
  const module = await import("@/features/data-hub/ui/DomainDetailPage");
  return { default: module.DomainDetailPage };
});

const ConnectionDetailPage = lazy(async () => {
  const module = await import("@/features/data-hub/ui/ConnectionDetailPage");
  return { default: module.ConnectionDetailPage };
});

const UsersPage = lazy(async () => {
  const module = await import("@/features/users/ui/UsersPage");
  return { default: module.UsersPage };
});

const RolesPage = lazy(async () => {
  const module = await import("@/features/roles/ui/RolesPage");
  return { default: module.RolesPage };
});

const MyProfilePage = lazy(async () => {
  const module = await import("@/features/users/ui/MyProfilePage");
  return { default: module.MyProfilePage };
});

const AppStudioPage = lazy(async () => {
  const module = await import("@/features/app-studio/ui/AppStudioPage");
  return { default: module.AppStudioPage };
});

const AppBuildPage = lazy(async () => {
  const module = await import("@/features/app-studio/ui/AppBuildPage");
  return { default: module.AppBuildPage };
});

const PublishedAppsPage = lazy(async () => {
  const module = await import("@/features/published-apps/ui/PublishedAppsPage");
  return { default: module.PublishedAppsPage };
});

const PublishedAppViewPage = lazy(async () => {
  const module = await import("@/features/published-apps/ui/PublishedAppViewPage");
  return { default: module.PublishedAppViewPage };
});

const NuggetsPage = lazy(async () => {
  const module = await import("@/features/nuggets/ui/NuggetsPage");
  return { default: module.NuggetsPage };
});

const ChatPage = lazy(async () => {
  const module = await import("@/features/chat");
  return { default: module.ChatPage };
});

const ChatHistoryPage = lazy(async () => {
  const module = await import("@/features/chat");
  return { default: module.ChatHistoryPage };
});

const CognitoCallbackPage = lazy(async () => {
  const module = await import("@/features/auth/ui/CognitoCallbackPage");
  return { default: module.CognitoCallbackPage };
});

const SharedAppViewPage = lazy(async () => {
  const module = await import("@/features/shared-apps/ui/SharedAppViewPage");
  return { default: module.SharedAppViewPage };
});

const SettingsPage = lazy(async () => {
  const module = await import("@/features/settings/ui/SettingsPage");
  return { default: module.SettingsPage };
});

const ActionsPage = lazy(async () => {
  const module = await import("@/features/actions/ui/ActionsPage");
  return { default: module.ActionsPage };
});

const ConnectionsHubPage = lazy(async () => {
  const module = await import("@/features/actions/ui/ConnectionsHubPage");
  return { default: module.ConnectionsHubPage };
});

const ContextHubPage = lazy(async () => {
  const module = await import("@/features/context-hub/ui/ContextHubPage");
  return { default: module.ContextHubPage };
});

function RouteFallback() {
  const { t } = useTranslation("common");
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight={240}
      aria-busy
      aria-label={t("loading")}
    >
      <CircularProgress />
    </Box>
  );
}

export function AppRoutes() {
  const isAdminHost = useIsAdminHost();

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public, chrome-less routes: outside RequireAuth (no login redirect) and outside
            MainLayout (no nav bar, no user avatar). */}
        <Route element={<BlankLayout />}>
          <Route path="/callback" element={<CognitoCallbackPage />} />
          <Route path="/shared/apps/:code" element={<SharedAppViewPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          {isAdminHost ? (
            <Route element={<AdminLayout />}>
              {adminRouteElements}
            </Route>
          ) : (
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route element={<RequireTab tab="data-hub" />}>
                <Route path="/data-hub" element={<DataHubPage />} />
                <Route path="/domains/:id" element={<DomainDetailPage />} />
                <Route path="/connections/:id" element={<ConnectionDetailPage />} />
              </Route>
              <Route element={<RequireTab tab="users" />}>
                <Route path="/users" element={<UsersPage />} />
              </Route>
              <Route element={<RequireTab tab="roles" />}>
                <Route path="/roles" element={<RolesPage />} />
              </Route>
              <Route path="/my-profile" element={<MyProfilePage />} />
              <Route element={<RequireTab tab="context-hub" />}>
                <Route path="/context-hub" element={<ContextHubPage />} />
              </Route>
              <Route element={<RequireTab tab="app-studio" />}>
                <Route path="/app-studio" element={<AppStudioPage />} />
                <Route path="/app-studio/:id" element={<AppBuildPage />} />
              </Route>
              <Route element={<RequireTab tab="nuggets" />}>
                <Route path="/nuggets" element={<NuggetsPage />} />
              </Route>
              <Route element={<RequireTab tab="apps" />}>
                <Route path="/apps" element={<PublishedAppsPage />} />
                <Route path="/apps/:id" element={<PublishedAppViewPage />} />
              </Route>
              <Route element={<RequireTab tab="actions" />}>
                <Route path="/actions" element={<ActionsPage />} />
                <Route path="/actions/connections" element={<ConnectionsHubPage />} />
              </Route>
              <Route element={<RequireTab tab="chats" />}>
                <Route path="/chat-history" element={<ChatHistoryPage />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/chat/:sessionId" element={<ChatPage />} />
              </Route>
              <Route element={<RequireTab tab="settings" />}>
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}
        </Route>
      </Routes>
    </Suspense>
  );
}
